import styles from "./OrganisationDocuments.module.scss";
import { useContext, useEffect, useState } from "react";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { Dropzone } from "../components/Dropzone/Dropzone";
import { useAlert } from "../context/AlertContext";
import { fetcher } from "../utils/fetcher";
import { AuthContext } from "../context/AuthContext";
import { FileWithPreview } from "../types/common";
import { useTranslation } from "../i18n";

interface Document {
  id: string;
  name: string;
  public_link: string;
  type: string;
}

export const OrganisationDocuments = () => {
  const authContext = useContext(AuthContext);
  const currentUser = authContext?.currentUser;
  const { setAlert } = useAlert();
  const { t } = useTranslation();
  const [documents, setDocuments] = useState<Document[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [files, setFiles] = useState<FileWithPreview[]>([]);

  const fetchOrgsDocuments = async () => {
    if (!currentUser?.userId) return;
    
    try {
      setLoading(true);
      const data = await fetcher<Document[]>({
        url: `/organisation/get-documents/${currentUser.userId}`,
      });
      setDocuments(data || []);
    } catch (error) {
      console.error("Error fetching organisation's documents:", error);
      setAlert(true, "error", t("newEvent.alerts.documentsLoadFailed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser?.userId) {
      fetchOrgsDocuments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.userId]);

  const handleAddDoc = async () => {
    try {
      if (currentUser?.userId && files.length > 0) {
        const formData = new FormData();
        formData.append("file", files[0]);

        await fetcher<void>({
          url: `/organisation/upload-document/${currentUser.userId}`,
          method: "POST",
          body: formData,
          noContentType: true,
        });
        await fetchOrgsDocuments();
        setAlert(true, "success", t("docs.alerts.added"));
        setFiles([]);
      }
    } catch (error) {
      console.error("Error adding new document:", error);
      setAlert(true, "error", t("docs.alerts.addFailed"));
    } finally {
      setDialogOpen(false);
    }
  };

  const handleDeleteDoc = async (doc: Document) => {
    try {
      if (currentUser?.userId) {
        await fetcher<void>({
          url: `/organisation/remove-custom-document/${currentUser.userId}?document_name=${doc.name}&document_id=${doc.id}`,
          method: "DELETE",
        });
        await fetchOrgsDocuments();
        setAlert(true, "success", t("docs.alerts.deleted"));
      }
    } catch (error) {
      console.error("Error deleting document:", error);
      setAlert(true, "error", t("docs.alerts.deleteFailed"));
    }
  };

  console.log("files", files);

  return (
    <div className={styles.documents}>
      <div className={styles.addNew}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setDialogOpen(true)}
        >
          {t("docs.addNew")}
        </Button>
      </div>
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </div>
      ) : (
        <div className={styles.cards}>
          {documents?.map((doc) => (
            <Card key={doc.id} className={styles.card}>
              <CardContent className={styles.cardContent}>
                <h3>{doc.name}</h3>
              </CardContent>
              <CardActions className={styles.cardActions}>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  startIcon={<FileDownloadOutlinedIcon />}
                  href={doc.public_link}
                >
                  {t("orgEvent.download")}
                </Button>
                {doc.type !== "global" && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteOutlinedIcon />}
                    onClick={() => handleDeleteDoc(doc)}
                  >
                    {t("common.remove")}
                  </Button>
                )}
              </CardActions>
            </Card>
          ))}
        </div>
      )}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        aria-labelledby="add-document-dialog-title"
        aria-describedby="add-document-dialog-description"
        maxWidth="xs"
        fullWidth
        className={styles.dialog}
      >
        <DialogTitle
          id="add-document-dialog-title"
          className={styles.dialogTitle}
        >
          {t("docs.newDocTitle")}
        </DialogTitle>
        <DialogContent className={styles.dialogContent}>
          <DialogContentText id="add-document-dialog-description">
            {t("docs.newDocHint")}
          </DialogContentText>
          <DialogContentText>
            {/* @ts-ignore - JS component */}
            <Dropzone
              fullWidth
              fileType="doc"
              files={files}
              setFiles={setFiles}
            />
          </DialogContentText>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button
            variant="outlined"
            color="inherit"
            fullWidth
            onClick={() => setDialogOpen(false)}
          >
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            fullWidth
            color="primary"
            onClick={handleAddDoc}
          >
            {t("docs.addDoc")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

